export type BuilderMediaContentTarget = {
  legacyUrlField: string | null;
  mediaUuidField: string;
  urlField: string;
};

type BuilderMediaBlock = {
  content?: Record<string, unknown>;
  options?: Record<string, unknown>;
  type: string;
};

function isCanonicalMediaBlock(blockType: string): boolean {
  return blockType === "image" || blockType === "media";
}

export function builderMediaUuidField(urlField: string): string {
  if (urlField === "image" || urlField === "imageUrl") {
    return "mediaUuid";
  }

  return urlField.endsWith("Url")
    ? `${urlField.slice(0, -3)}MediaUuid`
    : `${urlField}MediaUuid`;
}

export function builderMediaTargetForField(
  blockType: string,
  field: string,
): BuilderMediaContentTarget {
  const canonicalRootImage = isCanonicalMediaBlock(blockType)
    && (field === "image" || field === "imageUrl");
  const urlField = canonicalRootImage ? "image" : field;

  return {
    legacyUrlField: canonicalRootImage ? "imageUrl" : null,
    mediaUuidField: builderMediaUuidField(urlField),
    urlField,
  };
}

export function builderMediaTargetForPart(
  blockType: string,
  part: string,
): BuilderMediaContentTarget {
  return builderMediaTargetForField(
    blockType,
    part === "image" ? "imageUrl" : `${part}Url`,
  );
}

export function builderMediaContentValue(
  content: Record<string, unknown> | null | undefined,
  target: BuilderMediaContentTarget,
): string {
  const source = content ?? {};
  const canonicalValue = source[target.urlField];

  if (typeof canonicalValue === "string" && canonicalValue.trim() !== "") {
    return canonicalValue;
  }

  const legacyValue = target.legacyUrlField
    ? source[target.legacyUrlField]
    : null;

  return typeof legacyValue === "string" ? legacyValue : "";
}

export function withBuilderMediaContent(
  content: Record<string, unknown> | null | undefined,
  target: BuilderMediaContentTarget,
  url: string,
  mediaUuid: string | null,
): Record<string, unknown> {
  const nextContent = {
    ...(content ?? {}),
    [target.urlField]: url,
  };

  if (target.legacyUrlField && target.legacyUrlField !== target.urlField) {
    delete nextContent[target.legacyUrlField];
  }

  if (mediaUuid?.trim()) {
    nextContent[target.mediaUuidField] = mediaUuid;
  } else {
    delete nextContent[target.mediaUuidField];
  }

  return nextContent;
}

export function withBuilderMediaBlockContent<T extends BuilderMediaBlock>(
  block: T,
  target: BuilderMediaContentTarget,
  url: string,
  mediaUuid: string | null,
): T {
  const previousSource = builderMediaContentValue(block.content, target).trim();
  const nextBlock = {
    ...block,
    content: withBuilderMediaContent(block.content, target, url, mediaUuid),
  };
  const duplicateBackgroundKey = "imageBackgroundImage";
  const duplicateBackground = block.options?.[duplicateBackgroundKey];
  const hasConflictingAtomicBackground = isCanonicalMediaBlock(block.type)
    && target.urlField === "image"
    && previousSource !== ""
    && typeof duplicateBackground === "string"
    && duplicateBackground.trim() === previousSource;

  if (!hasConflictingAtomicBackground) {
    return nextBlock;
  }

  const nextOptions = { ...(block.options ?? {}) };

  delete nextOptions[duplicateBackgroundKey];
  delete nextOptions[builderMediaUuidField(duplicateBackgroundKey)];

  return {
    ...nextBlock,
    options: Object.keys(nextOptions).length > 0 ? nextOptions : undefined,
  };
}
